import React, { useCallback, useEffect, useState, FormEvent } from "react";

import classNames from "classnames";

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ProductData } from "data/product/products";
import { Button } from "studio-design-system";
import { v4 as uuidv4 } from "uuid";

import { ApiStatus } from "~/api/fetchStudioApi";
import * as productStyles from "~/components/videoProductPage/Product.css";
import { useCreatorEditing } from "~/hooks/useCreatorEditing";

import { SortableSkillsItem } from "./SortableSkillsItem";
import * as styles from "./WhatYouWillLearn.css";

export const WhatYouWillLearn = ({
  productData,
  isInEditMode,
  iconColor = "#000",
}: {
  productData: ProductData;
  isInEditMode: boolean;
  iconColor: string;
}): React.ReactElement => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 100 } }),
  );

  const {
    saveProductDraftUpdate,
    setSavingStatusText,
    setIsSavingChanges,
    CHANGES_SAVED_TEXT,
    CHANGES_ERROR_TEXT,
    CHANGES_LIMIT_TEXT,
    CHANGES_SAVING_TEXT,
  } = useCreatorEditing();
  const [skillsItems, setSkillsItems] = useState(
    productData.skills?.map((skill, index) => {
      return { text: skill, id: uuidv4() };
    }) || [],
  );

  useEffect(() => {
    setSkillsItems(
      productData.skills?.map((skill: string) => {
        return { text: skill, id: uuidv4() };
      }) || [],
    );
  }, [productData]);

  const handleAddNewItem = () => {
    setSkillsItems((prevSkills) => [...prevSkills, { text: "New Item", id: uuidv4() }]);
  };

  const SKILL_ITEM_MAX_LENGTH = 160;

  const handleDragOverAndEnd = useCallback(
    ({ active, over }: DragEndEvent | DragOverEvent) => {
      if (over !== null && active.id !== over.id) {
        setSkillsItems((items) => {
          const oldIndex = skillsItems.findIndex((x: any) => x.id === active.id);
          const newIndex = skillsItems.findIndex((x: any) => x.id === over.id);

          return arrayMove(items, oldIndex, newIndex);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skillsItems],
  );

  useEffect(() => {
    if (isInEditMode) {
      saveNewSkills(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsItems]);

  const saveNewSkills = (throttleSave: boolean = false) => {
    setIsSavingChanges(true);
    setSavingStatusText(CHANGES_SAVING_TEXT);
    saveProductDraftUpdate(
      productData,
      throttleSave,
      "skills",
      skillsItems.map((val) => val.text),
    ).then((res: { status: ApiStatus }) => {
      if (res.status == ApiStatus.SUCCESS) {
        setSavingStatusText(CHANGES_SAVED_TEXT);
      } else {
        setSavingStatusText(CHANGES_ERROR_TEXT);
      }
    });
  };

  const handleDeleteSkill = (itemId: string) => {
    setSkillsItems((prevSkills) => prevSkills.filter((x) => x.id !== itemId));
  };

  const handleSkillChange = (event: FormEvent<HTMLDivElement>, itemId: string) => {
    let newText = (event.target as HTMLDivElement).textContent || "";
    let tempSkills = [...skillsItems];
    let index = skillsItems.findIndex((x: any) => x.id === itemId);
    tempSkills[index].text = newText;
    setSkillsItems(tempSkills);
  };

  // This is called every time there's a character change.
  const handleSkillEdit = (event: FormEvent<HTMLDivElement>, itemId: string) => {
    let newText = (event.target as HTMLDivElement).textContent || "";

    if (newText.length > SKILL_ITEM_MAX_LENGTH) {
      setSavingStatusText(CHANGES_LIMIT_TEXT);
      event.preventDefault();
      return;
    } else {
      setIsSavingChanges(true);
      saveNewSkills(true);
    }
  };

  const halfLength = Math.ceil(skillsItems.length / 2);
  const firstHalf = skillsItems.slice(0, halfLength);
  const secondHalf = skillsItems.slice(halfLength);

  return (
    <section className={styles.whatYouLearnSection}>
      <div
        className={classNames(
          styles.whatYouLearnTitle,
          isInEditMode && productStyles.nonEditableElement,
        )}
      >
        What you’ll learn
      </div>
      <div className={styles.whatYouLearnItemsBlock}>
        <div className={styles.skillsColumns}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragOverAndEnd}
            onDragOver={handleDragOverAndEnd}
          >
            <SortableContext
              items={firstHalf.map((sI) => sI.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.skillsColumn}>
                {skillsItems &&
                  firstHalf.map((el, i) => (
                    <SortableSkillsItem
                      key={el.id}
                      id={el.id}
                      skillsText={el.text}
                      iconColor={iconColor}
                      itemIndex={i}
                      isInEditMode={isInEditMode}
                      handleSkillChange={handleSkillChange}
                      handleSkillEdit={handleSkillEdit}
                      handleDeleteSkill={handleDeleteSkill}
                    />
                  ))}
              </div>
            </SortableContext>
            <SortableContext
              items={secondHalf.map((sI) => sI.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.skillsColumn}>
                {skillsItems &&
                  secondHalf.map((el, i) => (
                    <SortableSkillsItem
                      key={el.id}
                      id={el.id}
                      skillsText={el.text}
                      itemIndex={i}
                      iconColor={iconColor}
                      isInEditMode={isInEditMode}
                      handleSkillChange={handleSkillChange}
                      handleSkillEdit={handleSkillEdit}
                      handleDeleteSkill={handleDeleteSkill}
                    />
                  ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
      {isInEditMode && (
        <Button onClick={handleAddNewItem} variant={"secondary"} className={styles.addItemButton}>
          + Add new item
        </Button>
      )}
    </section>
  );
};
